/**
 * Ported VERBATIM from `apr_template/src/pages/ai-analysis/ReportLibrary.tsx`.
 * Only import paths change. See ../compat/ for the shims.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PageHeader from '../compat/PageHeader';
import HelpPanel from '../compat/HelpPanel';
import { type Help } from '../compat/helpTypes';
import { useAuth } from '../compat/AuthContext';
import {
  type ReportArtifact, type CurrentReport,
  listReports, getPortfolioCurrent, generatePortfolioReport, generateRunReport, downloadReport,
} from '../compat/reportsApi';
import { listRuns, type AgentRun } from '../compat/aiRunsApi';

const fmt$ = (n?: number | string | null) =>
  n == null || n === '' || isNaN(Number(n)) ? '—' : `$${Math.round(Number(n)).toLocaleString('en-US')}`;
const fmtDateTime = (d?: string | null) => (d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');

const ReportHelp: Help = {
  title: 'Report Library',
  icon: '📑',
  purpose: 'The home for the client-facing Word and PowerPoint deliverables. Each analysis run and the portfolio-wide summary get a dated report generated once and cached here, so everyone downloads the same consistent document instead of re-rendering their own.',
  doHere: [
    'Download the <b>portfolio summary</b> (every application\'s current disposition) as Word or PowerPoint.',
    'Download the <b>per-run</b> report for any completed analysis.',
    'See the full <b>dated history</b> of every report generated, and who generated it.',
    '<b>Administrators</b> can regenerate a report when the underlying analysis has changed (shown as “stale”).',
  ],
  fits: 'Application Evaluation → Disposition Intelligence → Report Library',
  firstTime: 'Reports are generated automatically when an analysis run completes. Generating a document uses no AI — it re-formats analysis already on record.',
};

/* Small download button pair — Word (blue) / PPT (red), matching the app standard. */
function DownloadButtons({ artifact, busy, onDownload }: {
  artifact: ReportArtifact | null; busy: boolean; onDownload: (id: string, fmt: 'docx' | 'pptx') => void;
}) {
  if (!artifact) return <span style={{ fontSize: 12, color: '#90a4ae' }}>Not generated yet</span>;
  return (
    <div style={{ display: 'inline-flex', gap: 8 }}>
      <button className="rd-btn rd-btn-word" disabled={busy || !artifact.docx_key} onClick={() => onDownload(artifact.id, 'docx')} title="Download Word (.docx)">
        <i className="material-icons">description</i>Word
      </button>
      <button className="rd-btn rd-btn-ppt" disabled={busy || !artifact.pptx_key} onClick={() => onDownload(artifact.id, 'pptx')} title="Download PowerPoint (.pptx)">
        <i className="material-icons">slideshow</i>PPT
      </button>
    </div>
  );
}

function StalePill({ stale }: { stale: boolean }) {
  if (!stale) return (
    <span style={{ fontSize: 11, fontWeight: 700, color: '#2e7d32', background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: 20, padding: '2px 10px' }}>Up to date</span>
  );
  return (
    <span title="The analysis changed since this report was generated" style={{ fontSize: 11, fontWeight: 700, color: '#b45309', background: '#fff4e5', border: '1px solid #ffcc80', borderRadius: 20, padding: '2px 10px' }}>Data changed since generated</span>
  );
}

const ReportLibrary: React.FC = () => {
  const { currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'Administrator';

  const [reports, setReports] = useState<ReportArtifact[]>([]);
  const [runs, setRuns] = useState<AgentRun[]>([]);
  const [portfolio, setPortfolio] = useState<CurrentReport>({ artifact: null, stale: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');   // id/scope currently generating or downloading

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [reps, rns, pf] = await Promise.all([listReports(), listRuns(), getPortfolioCurrent()]);
      setReports(reps); setRuns(rns); setPortfolio(pf);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load the Report Library.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const doDownload = useCallback(async (id: string, fmt: 'docx' | 'pptx') => {
    setBusy(`dl-${id}-${fmt}`); setError('');
    try { await downloadReport(id, fmt); }
    catch (e: any) { setError(e?.response?.data?.error || e?.message || 'Download failed.'); }
    finally { setBusy(''); }
  }, []);

  const doGeneratePortfolio = useCallback(async () => {
    setBusy('portfolio'); setError('');
    try { await generatePortfolioReport(); await load(); }
    catch (e: any) { setError(e?.response?.data?.error || e?.message || 'Could not generate the portfolio report.'); }
    finally { setBusy(''); }
  }, [load]);

  const doGenerateRun = useCallback(async (runId: string) => {
    setBusy(`run-${runId}`); setError('');
    try { await generateRunReport(runId); await load(); }
    catch (e: any) { setError(e?.response?.data?.error || e?.message || 'Could not generate the run report.'); }
    finally { setBusy(''); }
  }, [load]);

  // Current (is_current) report per run + portfolio history.
  const currentByRun = useMemo(() => {
    const m = new Map<string, ReportArtifact>();
    reports.filter(r => r.scope === 'run' && r.is_current && r.run_id).forEach(r => m.set(r.run_id!, r));
    return m;
  }, [reports]);
  const portfolioHistory = useMemo(() => reports.filter(r => r.scope === 'portfolio'), [reports]);
  const completedRuns = useMemo(() => runs.filter(r => r.status === 'completed'), [runs]);

  return (
    <div className="page ai-results-page">
      <HelpPanel help={ReportHelp} />
      <PageHeader
        title="Report Library"
        subtitle="Client-facing Word & PowerPoint deliverables — generated once, downloaded by everyone. Creating a report uses no AI."
        actions={
          <>
            {isAdmin && (
              <button className="rd-btn rd-btn-primary" onClick={doGeneratePortfolio} disabled={busy === 'portfolio'}
                title={portfolio.artifact ? 'Regenerate the portfolio report from the latest data' : 'Generate the portfolio report'}>
                <i className="material-icons">{portfolio.artifact ? 'autorenew' : 'add'}</i>
                {busy === 'portfolio' ? 'Generating…' : portfolio.artifact ? 'Regenerate' : 'Generate'}
              </button>
            )}
            <button className="rd-btn rd-btn-ghost" onClick={load} disabled={loading}><i className="material-icons">refresh</i>{loading ? 'Loading…' : 'Refresh'}</button>
          </>
        }
      />

      {error && (
        <div style={{ margin: '0 20px 12px', padding: '12px 16px', background: '#ffebee', border: '1px solid #ffcdd2', borderRadius: 8, color: '#c62828', display: 'flex', gap: 10, alignItems: 'center' }}>
          <i className="material-icons" style={{ fontSize: 18 }}>error_outline</i><span>{error}</span>
          <button className="rd-btn rd-btn-ghost" style={{ marginLeft: 'auto', fontSize: 12 }} onClick={() => setError('')}>Dismiss</button>
        </div>
      )}

      {loading && reports.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9e9e9e' }}>Loading reports…</div>
      ) : (
        <>
          {/* ── Portfolio summary report ───────────────────────────────────────
              The heading block that stood here — the "Portfolio summary" eyebrow, the
              "Portfolio Rationalization Summary" title, its description and the
              "No portfolio report generated yet." empty state — was removed on the
              owner's instruction (2026-08-26), and the generate control moved up into
              the page header.

              What remains is only ever shown when there IS a report: the detail line,
              the download buttons and the history. So the card is gated on the artifact
              rather than always rendered — with nothing generated, all three parts drew
              nothing and the card read as an empty box, which is what the owner saw. */}
          {portfolio.artifact && (
          <div className="rd-card" style={{ margin: '4px 20px 18px', padding: '18px 20px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 260 }}>
                {portfolio.artifact && (
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12.5, color: '#455a64', alignItems: 'center' }}>
                    <StalePill stale={portfolio.stale} />
                    <span>{portfolio.artifact.app_count ?? '—'} applications</span>
                    <span>·</span>
                    <span style={{ color: '#2e7d32', fontWeight: 600 }}>{fmt$(portfolio.artifact.savings_mid)} savings</span>
                    <span>·</span>
                    <span>Generated {fmtDateTime(portfolio.artifact.generated_at)}{portfolio.artifact.generated_by_name ? ` by ${portfolio.artifact.generated_by_name}` : ''}</span>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <DownloadButtons artifact={portfolio.artifact} busy={!!busy} onDownload={doDownload} />
              </div>
            </div>
            {portfolioHistory.length > 1 && (
              <details style={{ marginTop: 14 }}>
                <summary style={{ cursor: 'pointer', fontSize: 12, color: '#607d8b' }}>History ({portfolioHistory.length} snapshots)</summary>
                <div style={{ marginTop: 8 }}>
                  {portfolioHistory.map((h) => (
                    <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '6px 0', borderTop: '1px solid #f0f0f0', fontSize: 12.5 }}>
                      <span style={{ color: '#455a64', minWidth: 190 }}>{fmtDateTime(h.generated_at)}</span>
                      <span style={{ color: '#78909c' }}>{h.app_count ?? '—'} apps · {fmt$(h.savings_mid)}</span>
                      {h.is_current && <span style={{ fontSize: 10, fontWeight: 800, color: '#fff', background: '#5e35b1', borderRadius: 20, padding: '1px 7px' }}>CURRENT</span>}
                      <span style={{ marginLeft: 'auto' }}><DownloadButtons artifact={h} busy={!!busy} onDownload={doDownload} /></span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
          )}

          {/* ── Per-run reports ──────────────────────────────────────────────── */}
          <div style={{ margin: '0 20px 8px', fontSize: 12, fontWeight: 700, color: '#607d8b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Per-run reports</div>
          <div style={{ margin: '0 20px 24px', overflowX: 'auto' }}>
            <table className="rd-table">
              <thead>
                <tr>
                  <th>Analysis run</th>
                  <th>Client</th>
                  <th>Status</th>
                  <th>Report generated</th>
                  <th style={{ textAlign: 'center' }}>Download</th>
                  {isAdmin && <th style={{ textAlign: 'right' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {completedRuns.length === 0 && (
                  <tr><td colSpan={isAdmin ? 6 : 5} style={{ textAlign: 'center', color: '#9e9e9e', padding: 28 }}>No completed analysis runs yet.</td></tr>
                )}
                {completedRuns.map((run) => {
                  const art = currentByRun.get(run.id) || null;
                  const generating = busy === `run-${run.id}`;
                  return (
                    <tr key={run.id}>
                      <td style={{ fontWeight: 600 }}>{run.name}</td>
                      <td className="rd-cell-muted">{(run as any).client_name || '—'}</td>
                      <td><span style={{ fontSize: 11, fontWeight: 700, color: '#2e7d32', background: '#e8f5e9', borderRadius: 20, padding: '2px 9px' }}>Completed</span></td>
                      <td className="rd-cell-muted">{art ? fmtDateTime(art.generated_at) : '—'}</td>
                      <td style={{ textAlign: 'center' }}><DownloadButtons artifact={art} busy={!!busy} onDownload={doDownload} /></td>
                      {isAdmin && (
                        <td style={{ textAlign: 'right' }}>
                          <button className="rd-btn rd-btn-ghost" onClick={() => doGenerateRun(run.id)} disabled={generating} style={{ fontSize: 12 }}
                            title={art ? 'Regenerate this run’s report' : 'Generate this run’s report'}>
                            <i className="material-icons">{art ? 'autorenew' : 'add'}</i>
                            {generating ? 'Generating…' : art ? 'Regenerate' : 'Generate'}
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default ReportLibrary;
